import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, ArrowRight } from "lucide-react";
import api from "@/lib/api";

const POPULAR = ["Linen", "Silk", "Trousers", "Bouclé", "Wool", "Bestsellers"];

export default function SearchModal({ open, onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 250);
    if (!open) { setQ(""); setResults([]); }
  }, [open]);

  // debounce
  useEffect(() => {
    if (!open) return;
    if (!q.trim()) { setResults([]); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/products", { params: { q: q.trim(), limit: 6 } });
        setResults(data.items || []);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(t);
  }, [q, open]);

  const submit = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    navigate(`/shop?q=${encodeURIComponent(q.trim())}`);
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[70]"
            onClick={onClose}
            data-testid="search-overlay"
          />
          <motion.div
            initial={{ y: "-100%" }} animate={{ y: 0 }} exit={{ y: "-100%" }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="fixed top-0 left-0 right-0 z-[71] bg-background border-b border-border max-h-[88vh] overflow-y-auto"
            data-testid="search-modal"
          >
            <div className="max-w-[1200px] mx-auto px-5 md:px-10 pt-6 md:pt-8 pb-12">
              {/* header */}
              <div className="flex items-center justify-between mb-6 md:mb-10">
                <div className="ie-overline text-brand-accent">Search the Atelier</div>
                <button onClick={onClose} aria-label="close search" data-testid="search-close" className="p-2 hover:opacity-60 transition-opacity">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* input */}
              <form onSubmit={submit} className="flex items-center gap-4 border-b border-foreground pb-3" data-testid="search-form">
                <Search className="w-5 h-5 text-text-secondary" />
                <input
                  ref={inputRef}
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="What are you looking for?"
                  className="flex-1 bg-transparent border-0 outline-none font-serif text-2xl md:text-4xl placeholder:text-text-secondary"
                  data-testid="search-input"
                />
                {q && (
                  <button type="button" onClick={() => setQ("")} className="text-text-secondary hover:text-foreground" aria-label="clear" data-testid="search-clear">
                    <X className="w-4 h-4"/>
                  </button>
                )}
              </form>

              {/* results / popular */}
              <div className="mt-8">
                {!q.trim() ? (
                  <div>
                    <div className="ie-overline text-text-secondary mb-4">Popular</div>
                    <div className="flex flex-wrap gap-2.5">
                      {POPULAR.map((term) => (
                        <button
                          key={term}
                          onClick={() => setQ(term)}
                          className="ie-overline border border-border px-4 py-2.5 hover:border-foreground hover:text-foreground text-text-secondary transition-colors"
                          data-testid={`search-popular-${term.toLowerCase()}`}
                        >
                          {term}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : loading ? (
                  <div className="ie-overline text-text-secondary py-8">Searching…</div>
                ) : results.length === 0 ? (
                  <div className="py-8 text-text-secondary" data-testid="search-empty">
                    <div className="font-serif text-2xl mb-2">No matches yet.</div>
                    <div className="text-sm">Try a different word — fabric, category, or piece.</div>
                  </div>
                ) : (
                  <div data-testid="search-results">
                    <div className="ie-overline text-text-secondary mb-4">{results.length} matches</div>
                    <ul className="divide-y divide-border">
                      {results.map((p) => (
                        <li key={p.id}>
                          <Link
                            to={`/product/${p.id}`}
                            onClick={onClose}
                            className="flex items-center gap-4 py-4 group"
                            data-testid={`search-result-${p.id}`}
                          >
                            <div className="w-16 h-20 bg-surface flex-shrink-0 overflow-hidden">
                              <img src={p.images[0]} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"/>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-serif text-lg md:text-xl truncate group-hover:text-brand-accent transition-colors">{p.name}</div>
                              <div className="ie-overline text-text-secondary mt-0.5">{p.subtitle}</div>
                            </div>
                            <div className="font-serif text-sm md:text-base flex-shrink-0">₹{p.price.toLocaleString("en-IN")}</div>
                            <ArrowRight className="w-4 h-4 hidden sm:block group-hover:translate-x-1 transition-transform"/>
                          </Link>
                        </li>
                      ))}
                    </ul>
                    <button onClick={submit} className="ie-overline text-brand-accent mt-6 hover:underline inline-flex items-center gap-2" data-testid="search-see-all">
                      See all results <ArrowRight className="w-3 h-3"/>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
